<template>
  <div
    v-if="app"
    class="build-list"
  >
    <portal :to="buildButtonPortal">
      <icon-menu>
        <apio-button
          slot="icon"
          class="mr-2"
          button-text-size="tiny"
          button-size="small"
          button-type="primary"
          button-icon=""
        >
          DOWNLOAD
          <apio-icon
            class="ml-1"
            name="ui-download-binary"
            lightModeColour="white"
            darkModeColour="white"
          />
        </apio-button>
        <div slot="icon-menu-list">
          <v-list-item
            v-for="(build, idx) in builds"
            :key="idx"
            @click="buildApp(build, app)"
            >{{ build.title }}
          </v-list-item>
        </div>
      </icon-menu>
    </portal>
    <div>
      <apio-switch
        :color="'purple'"
        :value="editInterfaceLabels"
        @change="toggleEditInterfaceLabels"
        ><span slot="left-label" class="tiny">Edit interface names</span>
      </apio-switch>
      <portal :to="app.appID">
        <div class="d-flex align-center">
          <status-indicator
            v-if="isRunning"
            status="positive"
            pulse
          ></status-indicator>
          <status-indicator
            v-if="!isRunning && !isUpgrading"
            status="intermediary"
          ></status-indicator>
          <status-indicator
            v-if="isUpgrading"
            status="active"
            pulse
          ></status-indicator>
        </div>
      </portal>
      <portal :to="upgradeButtonPortal">
        <apio-button
          v-if="isRunning"
          class="mr-2"
          button-text-size="tiny"
          button-size="small"
          button-type="primary"
          button-icon=""
          @click.native="runOTA"
          >UPGRADE
          <apio-icon
            class="ml-2"
            name="nav-cog"
            lightModeColour="white"
            darkModeColour="white"
          />
        </apio-button>
      </portal>
      <div v-for="(group, name) in app.app_groups" :key="name">
        <build-group :name="name" :interfaces="interfaces" :app="app" :editInterfaceLabels="editInterfaceLabels" :applicationStatus="applicationStatus" />
      </div>
    </div>

    <apio-button
      class="mr-2"
      button-text-size="tiny"
      button-size="small"
      button-type="secondary"
      button-icon=""
      @click="addNewGroupStatus = true"
      @mouseover.native="onSecondaryButtonMouseOver('newgroup')"
      @mouseout.native="onSecondaryButtonMouseOut('newgroup')"
      >ADD NEW GROUP
      <apio-icon
        class="ml-2"
        name="ui-folder"
        :lightModeColour="secondaryButtonIconColours.newgroup.color"
        :darkModeColour="secondaryButtonIconColours.newgroup.color"

      />
    </apio-button>

    <v-dialog persistent v-model="addInterfaceStatus" width="600">
      <v-card>
        <v-toolbar flat color="apioshade3" class="white--text h2 mb-6">
          <div class="white--text run-step__title">
            {{
              newInterfaceType === "interfaces"
                ? "Add new interface"
                : "Add new environment"
            }}
          </div>
          <v-spacer></v-spacer>
          <apio-button
            button-type="get-started"
            button-size="small"
            @click="addInterfaceStatus = false"
            >Close
            <apio-icon
              class="ml-2"
              size="16"
              name="cross"
              lightModeColour="white"
              darkModeColour="white"
          /></apio-button>
        </v-toolbar>
        <v-card-text class="pb-0">
          <validation-observer
            tag="form"
            ref="new"
            @submit.prevent="validateForm('new')"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
              :name="
                newInterfaceType === 'interfaces'
                  ? 'interface name'
                  : 'environment name'
              "
              tag="div"
            >
              <label for="interface-name">
                {{
                  newInterfaceType === "interfaces"
                    ? "New interface name"
                    : "New environment name"
                }}
              </label>
              <v-text-field
                id="interface-name"
                outlined
                dense
                hide-details="auto"
                required
                :placeholder="
                  newInterfaceType === 'interfaces'
                    ? 'Enter interface name'
                    : 'Enter environment name'
                "
                v-model="interfaceName"
                ref="inputModalInterface"
              />
              <transition name="fade">
                <span v-if="errors[0]">{{ errors[0] }}</span>
              </transition>
            </validation-provider>
          </validation-observer>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <apio-button
            button-type="secondary"
            button-size="medium"
            class="mr-2"
            @click="toggleNewInterfaceModal()"
            >Cancel</apio-button
          >
          <apio-button button-size="medium" @click="validateForm('new')"
            >Create</apio-button
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <apio-snackbar
      :text="snackBarText"
      :status="snackBarStatus"
      :snackbar="snackbar"
      @close="snackbar = false"
    />

    <v-dialog max-width="600" v-model="dialog">
      <v-card>
        <v-toolbar flat color="apioshade3" class="white--text h2 mb-6">
          <div class="white--text run-step__title">
            {{ dialogTitle }}
          </div>
          <v-spacer></v-spacer>
          <apio-button
            button-type="get-started"
            button-size="small"
            @click.native="dialog = false"
            >Close
            <apio-icon
              class="ml-2"
              size="16"
              name="cross"
              lightModeColour="white"
              darkModeColour="white"
          /></apio-button>
        </v-toolbar>
        <div class="px-4" id="loadscreen" hide-footer :title="dialogTitle">
          <p v-html="dialogText" />
        </div>
        <v-card-actions>
          <v-spacer />
          <apio-button
            class="mr-4"
            button-size="small"
            button-type="secondary"
            @click.native="copyToClipboard(dialogText)"
            >Copy to clipboard
          </apio-button>
          <apio-button button-size="small" @click.native="dialog = false"
            >Cancel</apio-button
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="600" v-model="building">
      <v-card>
        <v-toolbar flat color="apioshade3" class="white--text h2 mb-6">
          <div class="white--text run-step__title">Building Application</div>
          <v-spacer></v-spacer>
          <apio-button
            button-type="get-started"
            button-size="small"
            @click.native="building = false"
            >Close
            <apio-icon
              class="ml-2"
              size="16"
              name="cross"
              lightModeColour="white"
              darkModeColour="white"
          /></apio-button>
        </v-toolbar>
        <div class="px-4" id="loadscreen" hide-footer :title="dialogTitle">
          <p>Application {{ app.real_name }} is being built. Please wait.</p>
          <v-progress-linear
            color="primary"
            indeterminate
            size="64"
          ></v-progress-linear>
        </div>
        <v-card-actions>
          <v-spacer />
          <apio-button button-size="small" @click.native="building = false"
            >Close</apio-button
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="addNewGroupStatus" width="600">
      <v-card>
        <v-toolbar flat color="apioshade3" class="white--text h2 mb-6">
          <div class="white--text run-step__title">Add new group</div>
          <v-spacer></v-spacer>
          <apio-button
            button-type="get-started"
            button-size="small"
            @click="addNewGroupStatus = false"
            >Close
            <apio-icon
              class="ml-2"
              size="16"
              name="cross"
              lightModeColour="white"
              darkModeColour="white"
          /></apio-button>
        </v-toolbar>
        <v-card-text class="pb-0">
          <label for="group-name">
            Group name
          </label>
          <v-text-field
            id="group-name"
            outlined
            dense
            hide-details="auto"
            required
            placeholder="New group name"
            v-model="newGroupName"
          />
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <apio-button
            button-type="secondary"
            button-size="medium"
            class="mr-2"
            @click="addNewGroupStatus = false"
            >Cancel</apio-button
          >
          <apio-button button-size="medium" @click="saveNewGroup"
            >Create</apio-button
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import editor from '@/API/services/editor';
import { StatusIndicator } from "vue-status-indicator";
import request from '@/API/';
import { keyBy } from "lodash";
import Vue from "vue";
import dayjs from "dayjs";
import * as clipboard from "clipboard-polyfill/text";
import { saveAs } from "file-saver";

var utc = require("dayjs/plugin/utc"); // dependent on utc plugin
var timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);

export default {
  name: "BuildList",
  components: {
    StatusIndicator,
    IconMenu: () => import("@/components/Misc/IconMenu"),
    ApioSwitch: () => import("@/components/Misc/Switch.vue"),
    BuildGroup: () => import("@/components/BuildGroup/BuildGroup.vue"),
  },
  props: {
    app: {
      type: Object,
      required: true,
      default: () => {},
    },
    clientHash: {
      type: String,
      required: true,
      default: "",
    },
  },
  data: () => ({
    interfaces: {},
    applicationStatus: {
      upgrading: false,
      running: false,
      idx: null,
      item: null,
    },
    builds: [
      { title: "Linux", params: "&s=linux" },
      { title: "Docker Container", params: "&s=linux&t=true" },
      { title: "macos (x86)", params: "&s=darwin" },
      { title: "Windows", params: "&s=windows" },
    ],
    addInterfaceStatus: false,
    newInterfaceType: "",
    existingInterfaceType: "",
    interfaceName: "",
    newInterfaceName: "",
    currentInterface: null,
    deleteModalStatus: false,
    interfaceToDelete: {},
    addInterfaceDialog: false,
    error: "",
    editInterfaceLabels: false,
    snackbar: false,
    snackBarStatus: "",
    snackBarText: "",
    dialog: false,
    dialogText: "",
    dialogTitle: "",
    building: false,
    addNewGroupStatus: false,
    newGroupName: "",
    secondaryButtonIconColours: {
      newgroup: {
        color: "cyan",
        light: {
          normal: "cyan",
          hover: "black",
        },
        dark: {
          normal: "cyan",
          hover: "cyan",
        },
      },
    },
  }),
  mounted() {
    this.$root.$on("edit-env", this.selectVarEdit);
    this.$root.$on('deleteGroup', this.jsDeleteGroup);
    this.updateapp(this.app);
  },
  watch: {
    app(newapp) {
      this.updateapp(newapp);
    },
  },
  computed: {
    upgradeButtonPortal() {
      return `${this.app.appID}-upgrade`;
    },
    runInCloudPortal() {
      return `${this.app.appID}-cloud`;
    },
    buildButtonPortal() {
      return `${this.app.appID}-build`;
    },
    isRunning() {
      return this.applicationStatus.running;
    },
    isUpgrading() {
      return this.applicationStatus.upgrading;
    },
    getInterfacesForApp() {
      return this.app.interfaces.filter((intf) => intf.icon === "cogs") || [];
    },
    theme() {
      return this.$vuetify.theme.dark ? "dark" : "light";
    },
  },
  methods: {
    async jsDeleteGroup(groupName) {
      if (!Object.prototype.hasOwnProperty.call(this.app.app_groups, groupName)) {
        return
      }
      if (this.app.app_groups.length < 2) {
        return
      }
      if (this.app.app_groups[groupName] != '') {
        alert('This group is not empty, please delete or move interfaces into other groups.');
        return
      }
      delete this.app.app_groups[groupName];
      await this.deleteGroup(groupName);
    },
    async deleteGroup(groupToDelete) {
      try {
        const payload = {
          "label": groupToDelete,
          clientHash: this.clientHash,
          appHash: this.app.appID,
        }
        await editor.deleteGroup(payload);
        this.$store.dispatch("getApps", {
          c: this.$route.query.c,
          q: ""
        });
      } catch (err) {
        console.log(err);
      }
    },

    checkIfGroupExists(groupName) {
      return Object.prototype.hasOwnProperty.call(this.app.app_groups, groupName);
    },
    onSecondaryButtonMouseOver(index) {
      return (this.secondaryButtonIconColours[index].color =
        this.secondaryButtonIconColours[index][this.theme].hover);
    },
    onSecondaryButtonMouseOut(index) {
      return (this.secondaryButtonIconColours[index].color =
        this.secondaryButtonIconColours[index][this.theme].normal);
    },
    async saveNewGroup() {
      try {
        if(this.checkIfGroupExists(this.newGroupName)) {
          this.snackBarText = "Group name already in use";
          this.snackBarStatus = "error";
          this.snackbar = true;
          return
        }

        const payload = {
          clientHash: this.clientHash,
          appHash: this.app.appID,
          [this.newGroupName]: []
        };

        await editor.saveGroup(payload);
        this.alertsDialog = false;
        this.snackbar = true;
        this.snackBarText = `New group ${this.newGroupName} saved`;
        this.snackBarStatus = 'success'
        await this.$store.dispatch("getApps", {
          c: this.$route.query.c,
          q: "",
        });
        this.addNewGroupStatus = false;
      } catch(err) {
        console.log(err);
      }
      this.newGroupName = "";
    },
    async copyToClipboard(val) {
      const vm = this;
      await clipboard.writeText(val).then(
        function () {
          vm.snackBarText = "Copied to clipboard";
          vm.snackBarStatus = "success";
          vm.snackbar = true;
        },
        function () {
          vm.snackBarText = "Could not copy value to the clipboard";
          vm.snackBarStatus = "error";
          vm.snackbar = true;
        }
      );
    },
    unpublishedChanges(interfaceUpdateTimestamp = dayjs.tz().utc().unix()) {
      if (this.isRunning) {
        const lastAppTime = this.applicationStatus?.item?.t;
        if (lastAppTime) {
          const lastAppTimeUnix = dayjs.tz(lastAppTime).utc().unix();
          const lastInterfaceTimeUnix = dayjs
            .tz(interfaceUpdateTimestamp)
            .utc()
            .unix();

          if (lastInterfaceTimeUnix > lastAppTimeUnix) {
            return true;
          }
        }
        return false;
      }
    },
    async buildApp(build, app) {
      if (this.building) return;
      const vm = this;
      this.building = true;
      const buildLink = `${process.env.VUE_APP_BASE_URL}build?i=${app.appID}&c=${app.clientHash}&e=${app.live_env}${build.params}`;

      var xhr = new XMLHttpRequest();
      xhr.onreadystatechange = function () {
        if (this.readyState === 4 && this.status === 200) {
          // Action to be performed when the document is read;
          saveAs(xhr.response, `apio_${app.clientHash}_${app.appID}.zip`, {});
          vm.building = false;
        } else if (this.readyState === 4 && this.status !== 200) {
          xhr.response.text().then((obj) => {
            const error = JSON.parse(obj);
            var errorText = error.details;
            if (error.details == undefined) {
              errorText = obj;
            }
            vm.building = false;
            vm.dialogTitle = "Build Error";
            vm.dialogText = `The following error occurred during building: <br> ${errorText}`;
            vm.dialog = true;
          });
        }
      };

      xhr.onerror = function () {
        vm.building = false;
        vm.building = false;
        vm.dialogTitle = "Build Error";
        vm.dialogText = `The following error occurred during building: <br> ${xhr.response.details}`;
        vm.dialog = true;
      };

      xhr.open("GET", buildLink, true);
      xhr.setRequestHeader("Authorization", "Bearer "+sessionStorage.getItem('session'));
      xhr.responseType = "blob";
      xhr.withCredentials = true;
      xhr.send();
    },
    toggleEditInterfaceLabels() {
      this.editInterfaceLabels = !this.editInterfaceLabels;
    },
    updateapp(app) {
      this.interfaces = keyBy(
        app.interfaces.filter((item) => item.icon == "cogs"),
        "hash"
      );
      this.getApplicationStatus();
      setTimeout(this.getRunning, 200);
    },

    getInterfaceStatus(status) {
      return status ? "Enable interface" : "Disable interface";
    },
    async runInCloud() {
      const { appID } = this.app;
      await request.get(`/build/dump/${appID}`)
        .then(function (response) {
          request.post(`/cloud/`, response).then(resp => {
            console.log(resp);
          })
        })
        .catch((err) => {
          this.error = err;
        });
    },
    async runOTA() {
      const { item } = this.applicationStatus;
      const { clientHash, live_env, appID } = this.app;
      const vm = this;
      Vue.set(this.applicationStatus, "running", false);
      Vue.set(this.applicationStatus, "upgrading", true);

      const buildURL = `/build/ota/${appID}?c=${clientHash}&i=${appID}&s=${item?.s}&e=${live_env}&t=${item?.t}`;

      await request.get(buildURL)
        .then(function (response) {
          if (response.status !== 200) {
            response.json().then(function (data) {
              vm.dialogTitle = "Upgrade Error";
              vm.dialogText = `The following error occurred during upgrade: <br> ${data.details}`;
              vm.dialog = true;
            });

            return;
          } else {
            this.unpublishedChanges();
          }
        })
        .catch((err) => {
          this.error = err;
        })
        .finally(() => {
          Vue.set(vm.applicationStatus, "upgrading", false);
          setTimeout(vm.getApplicationStatus, 100); // TODO
        });
    },
    updateApp(appID, interfaceID) {
      this.$store.dispatch("updateApp", {
        appID: appID,
        interfaceID: interfaceID,
      });
    },
    getApplicationStatus() {
      if (this == undefined || this.app.appID == "") {
        return;
      }
      request
        .get("/ingest/list/" + this.app.appID)
        .then((res) => {
          if (
            Object.keys(res.data).length === 0 &&
            res.data.constructor === Object
          ) {
            return;
          }

          Vue.set(this.applicationStatus, "running", false);
          Vue.set(this.applicationStatus, "idx", null);
          Vue.set(this.applicationStatus, "item", null);

          for (const [key, value] of Object.entries(res.data)) {
            Vue.set(this.applicationStatus, "running", true);
            Vue.set(this.applicationStatus, "idx", key);
            Vue.set(this.applicationStatus, "item", value);
          }
        })
        .catch((err) => {
          this.error = err;
        });
    },
    selectVarEdit(selectedApp) {
      this.$router.push({
        path: "/editenv",
        query: {
          a: selectedApp.appID,
          i: selectedApp.live_env,
          c: this.$route.query.c,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-input__control,
::v-deep .v-input__slot,
::v-deep .v-text-field__slot {
  min-height: 32px !important;
  height: 32px;
}

.build-list {
  padding: 24px 40px;
  @media (max-width: 1024px) {
    padding: 16px;
  }

  &__header {
    align-items: center;
    display: flex;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }
}
</style>
